import Link from "next/link";
import { useRouter } from "next/router";
import { useAppSelector } from "../../../../services/hooks/useReduxHooks";
import { useTranslationI18 } from "../../../../services/hooks/useTranslationI18";
import { selectGuestTiersEnabled } from "../../../../services/redux/selectors/loyaltyServiceSelectors";
import { PagesEnum } from "../../../../utils/enums";
import { getPageLink } from "../../../../utils/help/navigation/navigation";
import {
  getCurrentLoyaltyTier,
  getTierColor,
  getTierRank,
  sortLoyaltyTiers,
} from "../../../../utils/loyalty/tiers";
import { RestaurantTypeEnum } from "../../../../utils/types";
import { LoyaltyTierBadge } from "../../../shared/LoyaltyTier/Badge/LoyaltyTierBadge";
import styles from "./HomeGreetingRow.module.scss";

export const HomeGreetingRow = (props: { user: string }) => {
  const { user } = props;
  const { t } = useTranslationI18();
  const router = useRouter();

  const username = useAppSelector((state) => state.userInfo.username);
  const phoneNumberInfo = useAppSelector(
    (state) => state.userInfo.phoneNumberInfo
  );
  const restaurantType = useAppSelector(
    (state) => state.config.restaurantInfo?.type
  );
  const guestTiersEnabled = useAppSelector(selectGuestTiersEnabled);
  const customerInfo = useAppSelector(
    (state) => state.loyaltyCustomerInfo.data
  );
  const tierSystem = useAppSelector((state) => state.loyaltyTierSystem.data);

  const isEcommerce = restaurantType === RestaurantTypeEnum.ECOMMERCE;
  const isSignedIn = !!(phoneNumberInfo.isValid && username) || user.length > 0;

  const tiers = sortLoyaltyTiers(tierSystem?.tiers);
  const currentTier = getCurrentLoyaltyTier(customerInfo, tierSystem);
  const tierColor =
    guestTiersEnabled && currentTier
      ? getTierColor(currentTier, tiers)
      : undefined;

  const firstName = (username || user)
    .toLocaleLowerCase()
    .replace(/-/g, " ")
    .split(" ")[0];

  return (
    <div className={styles.greeting_row}>
      <h1
        data-testid="user-greeting"
        className={`${styles.welcome_title} main_title`}
      >
        {currentTier && tierColor && (
          <LoyaltyTierBadge
            rank={getTierRank(currentTier, tiers)}
            color={tierColor}
            size="small"
            showRank={false}
          />
        )}
        {isSignedIn ? (
          <>
            <span>
              {isEcommerce
                ? t("common:category-hello")
                : t("common:category-hey")}
            </span>
            {/* Figma 14893:53515 reads "Hey Sandy," -- the comma closes the
                name, where the tab-bar greeting put it after the greeting. */}
            <p className={styles.username}>{firstName},</p>
          </>
        ) : (
          t("common:category-visitor-greeting")
        )}
      </h1>

      {/* Signed out there are no orders to see, and the page behind it would
          only offer its empty state. */}
      {isSignedIn && (
        <Link href={getPageLink(router, PagesEnum.Orders).as} legacyBehavior>
          <a className={styles.orders_link} data-testid="order-history-link">
            {t("common:order-history")}
          </a>
        </Link>
      )}
    </div>
  );
};
